/**
 * Review photo lightbox — Little Giants PK
 *
 * Customer review photos in the "What Our Customers Say" section render as
 * small 262x180 thumbnails. Before this, clicking one did nothing: there was
 * no click target at all, so the full photo was unreachable.
 *
 * The grid requests a 600px thumbnail from the API (?w=600) because the
 * originals are phone photos — 3072x3072 and ~3.8 MB. The full-resolution URL
 * is carried on data-full, and that is what the lightbox opens, so zooming
 * still shows the real photo rather than an upscaled thumbnail.
 *
 * This delegates clicks from anywhere in the document, so it works for cards
 * the section injects asynchronously after its API fetch — no need to re-bind
 * when more reviews load via "Load more".
 *
 * Accessibility: thumbnails become real buttons (Enter/Space work), focus is
 * moved into the dialog and returned to the thumbnail on close, background
 * scroll is locked, and Escape closes.
 */
(function () {
  'use strict';

  var overlay = null;
  var imgEl = null;
  var lastFocus = null;

  function build() {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.className = 'lgr-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Customer review photo');
    overlay.hidden = true;

    overlay.innerHTML =
      '<button type="button" class="lgr-lightbox__close" aria-label="Close photo">' +
        '<svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true">' +
          '<line x1="2" y1="2" x2="14" y2="14"/><line x1="14" y1="2" x2="2" y2="14"/>' +
        '</svg>' +
      '</button>' +
      '<div class="lgr-lightbox__spinner" aria-hidden="true"></div>' +
      '<img class="lgr-lightbox__img" alt="Customer review photo">';

    imgEl = overlay.querySelector('.lgr-lightbox__img');

    // Clicking the backdrop closes; clicking the photo itself does not.
    overlay.addEventListener('click', function (e) {
      if (e.target === imgEl) return;
      close();
    });

    imgEl.addEventListener('load', function () {
      overlay.classList.add('is-loaded');
    });
    imgEl.addEventListener('error', function () {
      overlay.classList.add('is-loaded', 'is-error');
    });

    document.body.appendChild(overlay);
  }

  /** Full-resolution URL for a thumbnail, falling back to whatever it shows. */
  function fullSrc(el) {
    return el.getAttribute('data-full') || el.currentSrc || el.src;
  }

  function open(src, alt) {
    build();
    lastFocus = document.activeElement;

    // Clear every transient class before showing. 'is-open' is cleared too:
    // close() bails early when the overlay is already hidden, which can leave
    // the class behind and make the next open skip its fade-in.
    overlay.classList.remove('is-open', 'is-loaded', 'is-error');
    imgEl.alt = alt || 'Customer review photo';
    imgEl.src = src;

    overlay.hidden = false;
    // next frame so the transition actually runs
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });

    document.documentElement.classList.add('lgr-lightbox-open');
    overlay.querySelector('.lgr-lightbox__close').focus();
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('lgr-lightbox-open');
    if (overlay.hidden) return;

    window.setTimeout(function () {
      overlay.hidden = true;
      imgEl.removeAttribute('src');
    }, 200);

    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  // Turn a thumbnail into a real button the first time we see it.
  function prepare(el) {
    if (el.dataset.lgrZoomReady === 'true') return;
    el.dataset.lgrZoomReady = 'true';
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    if (!el.getAttribute('aria-label')) {
      el.setAttribute('aria-label', 'Open customer review photo');
    }
  }

  function isThumb(el) {
    return el && el.classList && el.classList.contains('lgr-card__photo') && el.tagName === 'IMG';
  }

  document.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('.lgr-card__photo') : null;
    if (!isThumb(el)) return;
    e.preventDefault();
    open(fullSrc(el), el.alt);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var el = document.activeElement;
    if (!isThumb(el)) return;
    e.preventDefault();
    open(fullSrc(el), el.alt);
  });

  // Mark thumbnails as interactive as they appear (cards are injected async).
  function scan() {
    document.querySelectorAll('.lgr-card__photo').forEach(prepare);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
